import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';

import { serialNumbersService } from 'services';
import { ROUTES } from 'constants/routes';
import { CustomButton, CustomInput, Spinner } from 'components';

import './EditOriginalPdfForm.scss';
import { ORIGINAL_PDF_LIST } from '../../constants/originalPdfList';
import { SerialNumber } from '../../interfaces/serialNumber';
import axios from 'axios';
import { faqImageDate, faqImageLink } from 'assets/images';
import { useAppSelector } from '../../hooks';
import {
  originalValidationSchemeAgent,
  originalValidationSchemeCustomer,
} from '../../validation';
import { AnyObject, TestContext } from 'yup';

export const EditOriginalPdfForm = () => {
  const { sn, commissioningToken } = useParams();
  const navigate = useNavigate();
  const [errMessage, setErrMessage] = useState<string>('');

  const [isUpdateRequestLoading, setIsUpdateRequestLoading] =
    useState<boolean>(false);
  const [isSuccessfullyUpdated, setIsSuccessfullyUpdated] =
    useState<boolean>(false);

  useState<boolean>(false);

  const { user } = useAppSelector((state) => state.auth);

  const isNonAuthorizedUser = !user || user?.type === 'shipping company';

  const [initialValues, setInitialValues] = useState<SerialNumber>({
    imo_nr: '',
    schiffsname: '',
    produktionsdatum: '',
    sn_partner: '',
    commissioning_date: '',
    email: '',
    pin: '',
    preSet: '',
  } as SerialNumber);

  const getFilteredFields = () => {
    return ORIGINAL_PDF_LIST.filter((field) => {
      if (!initialValues.sn) return false;

      if (field.id === 'commissioning_date') {
        field.disabled =
          !!initialValues.commissioning_date && !initialValues.preSet;
        return true;
      }

      if (field.id === 'pin') {
        if (!initialValues.requiresPin) return false;

        return !commissioningToken;
      }

      if (field.id === 'produktionsdatum') {
        return initialValues.requiresProductionDate && !commissioningToken;
      }

      if (isNonAuthorizedUser && initialValues?.sn) return true;

      return (
        field.id !== 'email' && field.id !== 'space-4' && field.id !== 'space-5'
      );
    });
  };

  const [fields, setFields] = useState(getFilteredFields());

  const computerSnField = fields.find((o) => o.id === 'sn_partner');

  const snValidation = async (value: any, ctx: TestContext<AnyObject>) => {
    const sn = value.toString();

    const serial = await serialNumbersService.findSerial(sn);

    if (computerSnField) computerSnField.disabled = serial.typ !== 'Messzelle';

    return true;
  };

  const formik = useFormik({
    initialValues,
    onSubmit: async (values) => {
      setIsUpdateRequestLoading(true);
      setErrMessage('');
      try {
        values.commissioning_token = commissioningToken || '';
        if (!values.imo_nr) delete values.imo_nr;
        if (!values.schiffsname?.trim()) delete values.schiffsname;
        await serialNumbersService.update(values);

        setIsUpdateRequestLoading(false);
        setIsSuccessfullyUpdated(true);

        navigate(`${ROUTES.documents_overview}/${values.sn}`);
      } catch (e) {
        setIsUpdateRequestLoading(false);
        setIsSuccessfullyUpdated(false);
        if (axios.isAxiosError(e) && e.response)
          setErrMessage(e.response.data?.message || 'Something went wrong');
      }
    },
    enableReinitialize: true,
    validationSchema: isNonAuthorizedUser
      ? originalValidationSchemeCustomer(
          snValidation,
          !!initialValues.requiresProductionDate && !commissioningToken,
          !!initialValues.requiresPin && !commissioningToken
        )
      : originalValidationSchemeAgent(
          snValidation,
          !!initialValues.requiresProductionDate && !commissioningToken,
          !!initialValues.requiresPin && !commissioningToken
        ),
  });

  useEffect(() => {
    setFields(getFilteredFields());
  }, [user, commissioningToken, initialValues]);

  useEffect(() => {
    setIsSuccessfullyUpdated(false);

    (async () => {
      if (!sn) {
        return;
      }

      const response = await serialNumbersService
        .findSerial(sn)
        .catch(() => navigate(ROUTES.search_pdf));

      if (!response) {
        return;
      }

      setInitialValues({
        sn: response.sn,
        imo_nr: response.imo_nr ? Number(response.imo_nr) : '',
        schiffsname: response.schiffsname,
        produktionsdatum: '',
        email: '',
        preSet: response.preSet,
        sn_partner: response.sn_partner || '',
        commissioning_date: response.commissioning_date?.slice(0, 10) || '',
        pin: '',
        requiresPin: response.requiresPin,
        requiresProductionDate: response.requiresProductionDate,
      } as SerialNumber);

      if (computerSnField) {
        computerSnField.disabled = response.typ !== 'Messzelle';
      }
    })();
  }, [sn]);

  return (
    <>
      {initialValues.sn && (
        <div className="edit-pdf-container">
          <h1 className="edit-pdf__header">Edit calibration</h1>
          <form onSubmit={formik.handleSubmit}>
            <div className="edit-pdf-form-list">
              {fields.map(({ id, name, type, disabled }) => {
                const identifier = id as keyof SerialNumber;

                if (type === 'gab') return <div></div>;
                if (type === 'space')
                  return <div key={id} className="generate-pdf__space"></div>;

                return (
                  <div key={id}>
                    <CustomInput
                      id={id}
                      name={id}
                      disabled={disabled}
                      value={formik.values[identifier]}
                      onChange={formik.handleChange}
                      label={name}
                      type={type}
                    />
                    {formik.touched[identifier] &&
                      formik.errors[identifier] && (
                        <div className="edit-pdf__error-validation-box">
                          <p className="edit-pdf__error-validation-text">
                            {formik.errors[identifier]}
                          </p>
                        </div>
                      )}
                    {identifier === 'produktionsdatum' && (
                      <div className="generate-pdf__info-box">
                        <p className="generate-pdf__info-text">
                          The productions date is to find on the type plate of
                          the Measuring Cell and written in the Format (mm/yy).
                        </p>
                      </div>
                    )}
                    {identifier === 'commissioning_date' &&
                      initialValues.commissioning_date && (
                        <div className="generate-pdf__info-box">
                          {initialValues.preSet && (
                            <p className="generate-pdf__info-text">
                              The commissioning date is pre-set to the
                              calibration date, as we do not have any references
                              or documents that prove a different commissioning
                              date. If you would like to have your commissioning
                              date changed please contact:
                              <a href="mailto:post@deckma.com">
                                post@deckma.com
                              </a>
                            </p>
                          )}
                          {!initialValues.preSet && (
                            <p className="generate-pdf__info-text">
                              Please don&apos;t forget to document the
                              commissioning date in the Oil Record Book
                            </p>
                          )}
                        </div>
                      )}
                  </div>
                );
              })}
            </div>
            {!isUpdateRequestLoading && (
              <div className="edit-pdf__button-box">
                <CustomButton
                  type="submit"
                  text="Update calibration"
                  width="35%"
                />
              </div>
            )}
            {isUpdateRequestLoading && (
              <div className="edit-pdf__spinner-box">
                <Spinner />
              </div>
            )}
            {isSuccessfullyUpdated && (
              <div className="edit-pdf__success">Successfully updated</div>
            )}
            {errMessage && <div className="edit-pdf__error">{errMessage}</div>}
          </form>

          {fields.some((o) => o.id === 'pin') && (
            <div>
              <h3 className="edit-pdf__faq-header">FAQ:</h3>
              <p className="edit-pdf__faq-question">Where to find the PIN?</p>
              <p className="edit-pdf__faq-answer">
                The PIN is printed on the calibration confirmation. It is a 4
                digit PIN. <br />
                If there is no PIN on your calibration confirmation, please use
                the last 4 digits of your link:
              </p>
              <br />
              <img src={faqImageLink} width="550px" />
            </div>
          )}
          {fields.some((o) => o.id === 'produktionsdatum') && (
            <div>
              <h3 className="edit-pdf__faq-header">FAQ:</h3>
              <p className="edit-pdf__faq-question">
                Where to find the production date?
              </p>
              <p className="edit-pdf__faq-answer">
                The production date is to find on the Type Plate of your
                Measuring Cell:
              </p>
              <br />
              <img src={faqImageDate} width="550px" />
            </div>
          )}
        </div>
      )}
    </>
  );
};
